import React, { Component } from 'react'
import { Descriptions, Form, FormInstance, Select } from 'antd'
import { CharacteristicsConsumer, CharacteristicsContextData } from 'context/CharacteristicsContext'
import { ShelterConsumer, ShelterContextData } from 'context/ShelterContext'
import { sbListAnimalIcon } from 'services/integration'
import { DEFAULT_SELECT_FILTER_OPTION } from 'helpers/form-fields.helper'
import ShelterBuddyExternalField from '../ShelterBuddyExternalField'
import MappingStepper, { Step } from '../MappingStepper'
import { DIRECTION_LABEL, Flow, Partner } from '../shared/types'
import { resolveFlow } from '../shared/resolvers'
import { makeSbLoader } from '../shared/sbOptions'
import { ActiveField, IncomingStep, IntegrationField, PartnerField } from '../shared/fields'

/** Loads the Shelter Buddy icon list for an integration. */
const loadIcons = makeSbLoader(sbListAnimalIcon, 'icons')

type MappingCharacteristicFormProps = {
  form: FormInstance
  partnerList: Partner[] | null
  /** Save-in-progress flag, forwarded to the final-step Save button. */
  loading?: boolean
}

type MappingCharacteristicFormInnerProps = MappingCharacteristicFormProps & {
  shelterContext: ShelterContextData
  characteristicsContext: CharacteristicsContextData
}

type MappingCharacteristicFormInnerState = {
  flow: Flow
}

class MappingCharacteristicFormInner extends Component<MappingCharacteristicFormInnerProps, MappingCharacteristicFormInnerState> {
  constructor(props: MappingCharacteristicFormInnerProps) {
    super(props)
    this.state = {
      flow: resolveFlow(props.form, 'characteristic_uuid'),
    }
  }

  componentDidMount() {
    const { shelterContext, characteristicsContext, form } = this.props

    // Initial values may settle after the Form mounts — re-resolve the flow.
    const flow = resolveFlow(form, 'characteristic_uuid')
    if (flow !== this.state.flow) {
      this.setState({ flow })
    }

    if (!shelterContext.fetching && !shelterContext.shelters?.length) {
      shelterContext.requestShelterList({
        options: {
          limit: 9999
        },
        search: {
          _fields: ["id", "uuid", "name"],
          active: true,
          deleted: false,
          organization_integrations: {
            with: {
              _fields: ["id", "uuid", "description", "partner_key", "main", "shelter_id"],
              active: true,
              deleted: false,
            }
          }
        }
      })
    }

    if (!characteristicsContext.fetching && !characteristicsContext.characteristicsRaw) {
      characteristicsContext.requestCharacteristics()
    }
  }

  /** Resolve a shelter name for display, from the loaded shelter list. */
  shelterName = (uuid?: string) =>
    this.props.shelterContext.shelters?.find(s => s.uuid === uuid)?.name

  /** Resolve a partner display name. */
  partnerName = (key?: string) =>
    this.props.partnerList?.find(p => p.key === key)?.name || key

  /** Resolve a characteristic name for display, from the loaded list. */
  characteristicName = (uuid?: string) =>
    this.props.characteristicsContext.characteristicsRaw?.find(c => c.uuid === uuid)?.name

  // ----- Field groups -----

  /** Shelter is always required — the external icon list is loaded per shelter integration. */
  renderShelterField = () => {
    const { shelterContext } = this.props
    const shelterOptions = shelterContext.shelters?.map(shelter => ({
      label: shelter.name,
      value: shelter.uuid,
    }))

    return (
      <Form.Item
        label="Shelter"
        name="shelter_uuid"
        rules={[{ required: true, message: 'Missing a shelter' }]}
      >
        <Select
          showSearch
          filterOption={DEFAULT_SELECT_FILTER_OPTION}
          loading={shelterContext.fetching}
          placeholder="Select a shelter"
          options={shelterOptions}
        />
      </Form.Item>
    )
  }

  /** Adopets characteristic target — used by outbound and inbound. */
  renderCharacteristicField = () => {
    const { characteristicsContext } = this.props
    const characteristicOptions = characteristicsContext.characteristicsRaw?.map(characteristic => ({
      label: characteristic.name,
      value: characteristic.uuid,
    }))

    return (
      <Form.Item
        label="Characteristic"
        name="characteristic_uuid"
        rules={[{ required: true, message: 'Missing a characteristic' }]}
      >
        <Select
          loading={characteristicsContext.fetching}
          placeholder="Select a characteristic"
          options={characteristicOptions}
          showSearch
          filterOption={DEFAULT_SELECT_FILTER_OPTION}
        />
      </Form.Item>
    )
  }

  /** External (Shelter Buddy icon) target. */
  renderExternalTarget = () => {
    const { form } = this.props

    return (
      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) => prev.integration_uuid !== cur.integration_uuid}
      >
        {({ getFieldValue }) => {
          const integrationUuid: string | undefined = getFieldValue("integration_uuid")
          if (!integrationUuid) return null

          return (
            <ShelterBuddyExternalField
              form={form}
              integrationUuid={integrationUuid}
              loadExternalList={loadIcons}
              label="External"
              placeholder="Select an icon"
            />
          )
        }}
      </Form.Item>
    )
  }

  /** Inbound only: read-only display of the incoming external value. */
  renderIncoming = () => (
    <IncomingStep
      form={this.props.form}
      partnerList={this.props.partnerList}
      externalRowLabel="External Icon"
    />
  )

  // ----- Review -----

  renderReview = () => {
    const { form } = this.props

    const partnerKey = form.getFieldValue('partner_key')
    const shelterUuid = form.getFieldValue('shelter_uuid')
    const characteristicUuid = form.getFieldValue('characteristic_uuid')
    const name = form.getFieldValue('name')
    const externalId = form.getFieldValue('external_id')

    const ourSide = this.characteristicName(characteristicUuid) || '—'
    const externalSide = [name, externalId].filter(Boolean).join(' · ') || '—'
    const mappingSummary = `${ourSide} ↔ ${externalSide}`

    return (
      <>
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Direction">
            {DIRECTION_LABEL.BOTH}
          </Descriptions.Item>
          <Descriptions.Item label="Partner">{this.partnerName(partnerKey) || '—'}</Descriptions.Item>
          <Descriptions.Item label="Mapping">{mappingSummary}</Descriptions.Item>
          <Descriptions.Item label="Shelter">
            {this.shelterName(shelterUuid) || shelterUuid || '—'}
          </Descriptions.Item>
        </Descriptions>

        <ActiveField />
      </>
    )
  }

  // ----- Step definitions -----

  steps = (): Step[] => {
    const reviewStep: Step = {
      key: 'review',
      title: 'Review & Save',
      fields: [],
      content: this.renderReview,
    }

    if (this.state.flow === 'inbound') {
      return [
        {
          key: 'incoming',
          title: 'Incoming',
          fields: [],
          content: this.renderIncoming,
        },
        {
          key: 'our-side',
          title: 'Our side',
          fields: ['shelter_uuid', 'characteristic_uuid'],
          content: () => (
            <>
              {this.renderShelterField()}
              {this.renderCharacteristicField()}
            </>
          ),
        },
        reviewStep,
      ]
    }

    return [
      {
        key: 'our-side',
        title: 'Our side',
        fields: ['partner_key', 'shelter_uuid', 'characteristic_uuid'],
        content: () => (
          <>
            <PartnerField partnerList={this.props.partnerList} only="SHELTER_BUDDY" />
            {this.renderShelterField()}
            {this.renderCharacteristicField()}
          </>
        ),
      },
      {
        key: 'external',
        title: 'External target',
        fields: ['external_id', 'name'],
        content: () => (
          <>
            <IntegrationField shelterContext={this.props.shelterContext} allowClear={false} />
            {this.renderExternalTarget()}
          </>
        ),
      },
      reviewStep,
    ]
  }

  render() {
    return <MappingStepper form={this.props.form} steps={this.steps()} loading={this.props.loading} />
  }
}

export default class MappingCharacteristicForm extends Component<MappingCharacteristicFormProps> {
  render() {
    return (
      <CharacteristicsConsumer>
        {(characteristicsContext) => (
          <ShelterConsumer>
            {(shelterContext) => (
              <MappingCharacteristicFormInner
                form={this.props.form}
                partnerList={this.props.partnerList}
                loading={this.props.loading}
                characteristicsContext={characteristicsContext}
                shelterContext={shelterContext}
              />
            )}
          </ShelterConsumer>
        )}
      </CharacteristicsConsumer>
    )
  }
}
